import { GearBaseIcon } from '@tableau/icons/GearBaseIcon';
import { DropdownButton, DropdownButtonType, ToggleSwitchWidget } from '@tableau/widgets';
import React from 'react';

import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { PageHeaderStyles } from './PageHeaderStyles';

const DARK_SESSION_VIZ_THEME = 'DARK';

export const VIZ_VIEWER_APPEARANCE_TEST_ID = 'viz-viewer-appearance';
export const VIZ_VIEWER_APPEARANCE_PANEL_ID = `${VIZ_VIEWER_APPEARANCE_TEST_ID}-panel`;
export const VIZ_VIEWER_APPEARANCE_DESCRIPTION_ID = `${VIZ_VIEWER_APPEARANCE_TEST_ID}-description`;

export function isCurrentVizThemeDark(): boolean {
  return SessionUserSelectors.viewerControlledVizTheme(getAppStore().getState()) === DARK_SESSION_VIZ_THEME;
}

export interface VizViewerAppearanceMenuProps {
  readonly displayOnlyIcons: boolean;
}

export const VizViewerAppearanceMenu: React.FC<VizViewerAppearanceMenuProps> = ({ displayOnlyIcons }) => {
  const [keepPreferredAppearance, setKeepPreferredAppearance] = React.useState<boolean>(isCurrentVizThemeDark);

  const dropdownRef = React.useRef<DropdownButton>(null);
  const repositionPopup = React.useCallback(() => {
    dropdownRef.current?.repositionPopup();
  }, []);
  React.useEffect(() => {
    window.addEventListener('resize', repositionPopup);
    return () => window.removeEventListener('resize', repositionPopup);
  }, [repositionPopup]);
  React.useLayoutEffect(repositionPopup, [displayOnlyIcons, repositionPopup]);

  if (!FeatureFlags.viewerControlledThemes_ViewingModeEnabled || !FeatureFlags.viewerControlledThemes_VizAreaEnabled || !FeatureFlags.viewerControlledThemes_VizThemeToggle) {
    return null;
  }

  if (!isCurrentVizThemeDark()) {
    return null;
  }

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeepPreferredAppearance(!event.target.checked);
  };

  const buttonLabel = Messages.vizViewerAppearance_buttonLabel();

  const renderLabel = (text: string, isActive: boolean) => (
    <span
      className={isActive ? PageHeaderStyles.VizViewerAppearanceMenuLabelText : PageHeaderStyles.VizViewerAppearanceMenuHiddenLabelText}
      aria-hidden={!isActive}
    >
      {text}
    </span>
  );

  const menuPanel = (
    <div
      id={VIZ_VIEWER_APPEARANCE_PANEL_ID}
      role='dialog'
      aria-label={buttonLabel}
      className={PageHeaderStyles.VizViewerAppearanceMenuPanel}
      data-tb-test-id={VIZ_VIEWER_APPEARANCE_TEST_ID}
    >
      <span id={VIZ_VIEWER_APPEARANCE_DESCRIPTION_ID} className='sr-only'>
        {Messages.vizViewerAppearance_switchDescription()}
      </span>
      <div className={PageHeaderStyles.VizViewerAppearanceMenuContent}>
        <div className={PageHeaderStyles.VizViewerAppearanceMenuLabel}>
          {renderLabel(Messages.vizViewerAppearance_showPublishedLabel(), !keepPreferredAppearance)}
          {renderLabel(Messages.vizViewerAppearance_showCustomizedLabel(), keepPreferredAppearance)}
        </div>
        <ToggleSwitchWidget
          aria-label={Messages.vizViewerAppearance_switchLabel()}
          aria-describedby={VIZ_VIEWER_APPEARANCE_DESCRIPTION_ID}
          className={PageHeaderStyles.VizViewerAppearanceMenuSwitch}
          checked={!keepPreferredAppearance}
          onChange={handleSwitchChange}
          testId={`${VIZ_VIEWER_APPEARANCE_TEST_ID}-switch`}
        />
      </div>
    </div>
  );

  return (
    <div className={PageHeaderStyles.VizViewerAppearanceMenuContainer}>
      <DropdownButton
        aria-controls={VIZ_VIEWER_APPEARANCE_PANEL_ID}
        aria-haspopup='dialog'
        aria-label={buttonLabel}
        className={PageHeaderStyles.VizViewerAppearanceMenuButton}
        closeOnPopupBlur={true}
        focusOnClose={true}
        label={undefined}
        popupAlignment='right'
        popupContent={menuPanel}
        ref={dropdownRef}
        testId={`${VIZ_VIEWER_APPEARANCE_TEST_ID}-button`}
        title={buttonLabel}
        type={DropdownButtonType.Icon}
      >
        <span className={PageHeaderStyles.TextButton}>
          <GearBaseIcon
            size={18}
            alt=''
            color='currentColor'
            style={displayOnlyIcons ? PageHeaderStyles.iconOnlyButton : PageHeaderStyles.iconForTextButton}
          />
          {!displayOnlyIcons && buttonLabel}
        </span>
      </DropdownButton>
    </div>
  );
};
